import { useEffect, useState, useRef } from 'react';
import { Table } from '@tanstack/react-table';

/**
 * Hook personalizzato per tracciare specifici cambiamenti dello state di TanStack Table.
 * Usa useSyncExternalStore pattern per un tracking efficiente e preciso.
 */
export function useTableColumnPinning<T>(table: Table<T>) {
  const [columnPinning, setColumnPinning] = useState(
    () => table.getState().columnPinning,
  );

  const lastColumnPinningRef = useRef(columnPinning);

  useEffect(() => {
    // Controlla i cambiamenti in modo più efficiente
    const checkForChanges = () => {
      const currentColumnPinning = table.getState().columnPinning;

      // Deep comparison ottimizzata usando JSON.stringify
      const currentStr = JSON.stringify(currentColumnPinning);
      const lastStr = JSON.stringify(lastColumnPinningRef.current);

      if (currentStr !== lastStr) {
        lastColumnPinningRef.current = currentColumnPinning;
        setColumnPinning(currentColumnPinning);
      }
    };

    // Controlla immediatamente
    checkForChanges();

    // Setup polling più efficiente - solo quando necessario
    const interval = setInterval(checkForChanges, 50); // Più frequente ma comunque performante

    return () => {
      clearInterval(interval);
    };
  }, [table]);

  return columnPinning;
}

/**
 * Hook più generico per tracciare qualsiasi parte dello state della tabella
 */
export function useTableState<T, TState>(
  table: Table<T>,
  selector: (state: any) => TState,
) {
  const [state, setState] = useState(() => selector(table.getState()));

  useEffect(() => {
    const updateState = () => {
      const newState = selector(table.getState());
      setState(prevState => {
        if (JSON.stringify(prevState) !== JSON.stringify(newState)) {
          return newState;
        }
        return prevState;
      });
    };

    const interval = setInterval(updateState, 100);
    return () => clearInterval(interval);
  }, [table, selector]);

  return state;
}
